/**
 * useWatchlistActions Hook
 *
 * Provides shared watchlist functionality for adding/removing symbols from portfolios.
 * Used by LatestTradesCard and TradesHistoryPage.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Portfolios, savePortfolio, updatePortfolio, Portfolio, getPlaidPortfolioIds } from '../storage';

interface UseWatchlistActionsResult {
  /** Whether the modal for selecting multiple portfolios is open */
  isWatchlistModalOpen: boolean;
  /** Open the portfolio selection modal */
  openWatchlistModal: () => void;
  /** Close the portfolio selection modal */
  closeWatchlistModal: () => void;
  /** The Plaid portfolio ID (excluded from manual edits) — first ID for backwards compat */
  plaidPortfolioId: string | null;
  /** All Plaid portfolio IDs (excluded from manual edits) */
  plaidPortfolioIds: string[];
  /** Non-Plaid portfolios available for manual editing */
  nonPlaidPortfolios: Portfolio[];
  /** Check if a symbol is in any watchlist */
  isInWatchlist: (symbol: string) => boolean;
  /** Handle the "Add to Watchlist" button click */
  handleAddToWatchlist: (symbol: string) => Promise<void>;
  /** Handle portfolio selection modal submission */
  handlePortfolioSubmit: (symbol: string, selectedIds: string[]) => Promise<void>;
}

export const useWatchlistActions = (): UseWatchlistActionsResult => {
  const [isWatchlistModalOpen, setIsWatchlistModalOpen] = useState(false);
  const [plaidPortfolioIdsList, setPlaidPortfolioIdsList] = useState<string[]>([]);
  const portfolios = Portfolios.useState((s) => s.portfolios);

  // Backwards compat: first Plaid portfolio ID
  const plaidPortfolioId = plaidPortfolioIdsList[0] || null;

  // Fetch all Plaid portfolio IDs on mount
  useEffect(() => {
    const fetchIds = async () => {
      const ids = await getPlaidPortfolioIds();
      setPlaidPortfolioIdsList(ids);
    };
    fetchIds();
  }, []);

  // Set of Plaid IDs for fast lookup
  const plaidIdSet = useMemo(() => new Set(plaidPortfolioIdsList), [plaidPortfolioIdsList]);

  // Get non-Plaid portfolios for manual editing
  const nonPlaidPortfolios = useMemo(() => portfolios.filter((p) => !plaidIdSet.has(p.id)), [portfolios, plaidIdSet]);

  // Check if symbol is in any portfolio
  const isInWatchlist = useCallback(
    (symbol: string) => {
      return portfolios.some((p) => p.symbols && Array.isArray(p.symbols) && p.symbols.includes(symbol));
    },
    [portfolios],
  );

  // Create default watchlist and add symbol
  const createDefaultWatchlist = useCallback(async (symbol: string) => {
    try {
      const defaultPortfolio: Omit<Portfolio, 'id'> = {
        title: 'Watchlist',
        strategy: 'Default watchlist',
        symbols: [symbol],
        allocations: [],
      };
      await savePortfolio(defaultPortfolio);
      toast(`${symbol} was added to Watchlist`);
    } catch (error) {
      console.error('Error creating default portfolio:', error);
      toast.error('Failed to add to watchlist');
    }
  }, []);

  // Add symbol to a single portfolio (only adds, doesn't toggle/remove)
  const addToSinglePortfolio = useCallback(
    async (symbol: string, portfolio: Portfolio) => {
      try {
        if (plaidIdSet.has(portfolio.id)) {
          toast.error('This portfolio cannot be edited manually');
          return;
        }
        const currentSymbols = portfolio.symbols || [];

        // If already in portfolio, do nothing (button state shows "✓ In Watchlist")
        if (currentSymbols.includes(symbol)) {
          return;
        }

        const updatedPortfolio: Portfolio = {
          ...portfolio,
          symbols: [...currentSymbols, symbol],
        };

        const success = await updatePortfolio(updatedPortfolio);
        if (success) {
          toast(`${symbol} was added to ${portfolio.title}`);
        } else {
          toast.error('Failed to update watchlist');
        }
      } catch (error) {
        console.error('Error updating portfolio:', error);
        toast.error('Failed to update watchlist');
      }
    },
    [plaidIdSet],
  );

  // Handle add to watchlist button click
  const handleAddToWatchlist = useCallback(
    async (symbol: string) => {
      if (nonPlaidPortfolios.length === 0) {
        await createDefaultWatchlist(symbol);
      } else if (nonPlaidPortfolios.length === 1) {
        await addToSinglePortfolio(symbol, nonPlaidPortfolios[0]);
      } else {
        setIsWatchlistModalOpen(true);
      }
    },
    [nonPlaidPortfolios, createDefaultWatchlist, addToSinglePortfolio],
  );

  // Handle portfolio selection modal submit
  const handlePortfolioSubmit = useCallback(
    async (symbol: string, selectedIds: string[]) => {
      try {
        const effectiveSelectedIds = selectedIds.filter((id) => !plaidIdSet.has(id));
        const portfolios = Portfolios.getRawState().portfolios;

        // Build list of updates to perform (each portfolio is independent)
        const updates: { portfolio: Portfolio; action: 'add' | 'remove' }[] = [];

        for (const portfolio of portfolios) {
          if (!portfolio || !portfolio.id) continue;
          if (plaidIdSet.has(portfolio.id)) continue;

          const currentSymbols = Array.isArray(portfolio.symbols) ? portfolio.symbols : [];
          const isSelected = effectiveSelectedIds.includes(portfolio.id);
          const isCurrentlyInPortfolio = currentSymbols.includes(symbol);

          if (isSelected && !isCurrentlyInPortfolio) {
            updates.push({
              portfolio: {
                ...portfolio,
                symbols: [...currentSymbols, symbol],
              },
              action: 'add',
            });
          } else if (!isSelected && isCurrentlyInPortfolio) {
            updates.push({
              portfolio: {
                ...portfolio,
                symbols: currentSymbols.filter((s) => s !== symbol),
                allocations: (portfolio.allocations || []).filter((a) => a.symbol !== symbol),
              },
              action: 'remove',
            });
          }
        }

        // Execute all updates in parallel (safe since each targets a different portfolio)
        await Promise.all(updates.map((u) => updatePortfolio(u.portfolio)));

        // Show toast based on actual changes made
        const addedCount = updates.filter((u) => u.action === 'add').length;
        const removedCount = updates.filter((u) => u.action === 'remove').length;

        if (addedCount > 0) {
          if (addedCount === 1) {
            const addedPortfolio = updates.find((u) => u.action === 'add')!.portfolio;
            toast(`${symbol} was added to ${addedPortfolio.title}`);
          } else {
            toast(`${symbol} was added to ${addedCount} watchlists`);
          }
        } else if (removedCount > 0) {
          if (removedCount === 1) {
            toast(`${symbol} was removed from 1 watchlist`);
          } else {
            toast(`${symbol} was removed from ${removedCount} watchlists`);
          }
        }
        // No toast if no changes were made
      } catch (error) {
        console.error('Error updating portfolios:', error);
        toast.error('Failed to update watchlists');
      }
    },
    [plaidIdSet],
  );

  return {
    isWatchlistModalOpen,
    openWatchlistModal: useCallback(() => setIsWatchlistModalOpen(true), []),
    closeWatchlistModal: useCallback(() => setIsWatchlistModalOpen(false), []),
    plaidPortfolioId,
    plaidPortfolioIds: plaidPortfolioIdsList,
    nonPlaidPortfolios,
    isInWatchlist,
    handleAddToWatchlist,
    handlePortfolioSubmit,
  };
};

export default useWatchlistActions;
